import { Alert, Button, Spin, Table, notification } from "antd";
import type { TFunction } from "i18next";
import moment from "moment-timezone";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ChangeLog } from "~/api/graphql";
import { useChangeLogsQuery } from "~/api/graphql/queries/changeLogs";
import ActionLogDrawerAsync from "~/components/common/ActionLogDrawerAsync";
import { formatDateTime } from "~/components/common/ActionLogDrawer/helpers";
import FilterBoxCard from "~/components/common/Card/FilterBoxCard";
import { ContentWrapper } from "~/components/common/styledComponents";
import DateRangePicker, {
  DateValues,
} from "~/components/common/DateRangePicker";
import { BERLIN_TIMEZONE } from "~/components/common/helpers";
import { SAVE_DATE_FORMAT } from "~/constants";
import { downloadCSV } from "~/components/pages/CommandHubView/utils";
import { ExportConflictError, serviceTrackerExportApi } from "./api";
import { ConflictModal } from "./ConflictModal";
import { formatRange } from "./conflicts";
import { formatEuro, PreviewModal } from "./PreviewModal";
import {
  Controls,
  Description,
  HistorySection,
  LogsButton,
  PageHeader,
  PeriodSelects,
  Title,
} from "./styles";
import {
  ExportConflict,
  PastExportRun,
  ServiceTrackerExportMode,
  ServiceTrackerExportResult,
} from "./types";

/*
 * The audit trail keys a cross-customer command under "ALL" rather than a
 * customer id (see `logAdminCommand` in kontax-backend).
 */
const EXPORT_AUDIT_RECORD_ID = "ALL";

const EXPORT_COMMAND_NAME = "exportTaxServices";

/*
 * Shape of `changes.__command.parameters` on an "exportTaxServices"
 * AdminCommand ChangeLog row. `from`/`to` are the current, ranged shape;
 * `year`/`month` are what a run written before this ticket shipped still
 * carries. Both can theoretically be present on old rows written mid-deploy,
 * so callers check `from`/`to` first.
 */
interface ExportCommandParameters {
  from?: string;
  to?: string;
  year?: number;
  month?: number;
  positions?: number;
  invoices?: number;
  customers?: number;
  grossTotalInEuro?: number;
  skippedFreePositions?: number;
}

/* The end of a previous run, whichever shape it was recorded in — this is the
 * single anchor both the prefill and the warnings compare against. */
interface RunAnchor {
  from: string;
  to: string;
  changedAt: string;
  changedBy?: string;
}

const getExportParameters = (
  log: ChangeLog
): ExportCommandParameters | null => {
  const command = log.changes?.__command;
  if (!command || command.name !== EXPORT_COMMAND_NAME || !command.parameters) {
    return null;
  }
  return command.parameters as ExportCommandParameters;
};

const toPastExportRun = (log: ChangeLog): PastExportRun | null => {
  const parameters = getExportParameters(log);
  if (!parameters?.from || !parameters?.to) {
    return null;
  }
  return {
    from: parameters.from,
    to: parameters.to,
    changedAt: log.changedAt,
    changedBy: log.changedBy,
    summary: {
      positions: parameters.positions ?? 0,
      invoices: parameters.invoices ?? 0,
      customers: parameters.customers ?? 0,
      grossTotalInEuro: parameters.grossTotalInEuro ?? 0,
      skippedFreePositions: parameters.skippedFreePositions ?? 0,
    },
  };
};

/* Derives the calendar month's start/end so a legacy run's "Bis" is defined. */
const toLegacyRunAnchor = (log: ChangeLog): RunAnchor | null => {
  const parameters = getExportParameters(log);
  if (parameters?.year == null || parameters?.month == null) {
    return null;
  }
  const start = moment.tz(
    { year: parameters.year, month: parameters.month - 1, day: 1 },
    BERLIN_TIMEZONE
  );
  return {
    from: start.format(SAVE_DATE_FORMAT),
    to: moment(start).endOf("month").format(SAVE_DATE_FORMAT),
    changedAt: log.changedAt,
    changedBy: log.changedBy,
  };
};

/*
 * Every "exportTaxServices" row as a RunAnchor, whichever shape it was
 * recorded in — a ranged row converts directly, a legacy row through its
 * derived month end. Used to pick the anchor across BOTH shapes (SMT-1364
 * review: a newer legacy run must not be shadowed by an older ranged one),
 * while `rangedRuns` (below) stays ranged-only for the "Frühere Exporte"
 * table.
 */
const toRunAnchor = (log: ChangeLog): RunAnchor | null => {
  const parameters = getExportParameters(log);
  if (parameters?.from && parameters?.to) {
    return {
      from: parameters.from,
      to: parameters.to,
      changedAt: log.changedAt,
      changedBy: log.changedBy,
    };
  }
  return toLegacyRunAnchor(log);
};

/*
 * Von = the day after the last run's Bis, Bis = yesterday. Fallback chain:
 * latest ranged run -> latest legacy run's derived month end -> the previous
 * calendar month, which is today's (pre-ticket) behaviour for an operator who
 * has never run an export before.
 */
const computePrefill = (anchor: RunAnchor | null): DateValues => {
  if (anchor) {
    const dateFrom = moment(anchor.to).add(1, "day").format(SAVE_DATE_FORMAT);
    const yesterday = moment
      .tz(BERLIN_TIMEZONE)
      .subtract(1, "day")
      .format(SAVE_DATE_FORMAT);

    /*
     * Bis is yesterday, EXCEPT when that lies before Von, which is the state
     * right after any normal run: exporting up to yesterday and reopening the
     * page the same day puts Von on today and Bis on yesterday. Prefilling that
     * would open the page on a reversed range, which the endpoint rejects, and
     * the date control silently swaps the two for display, so the form would
     * show a range the export never used. A single day is the honest reading of
     * "nothing new is billable yet".
     */
    return {
      dateFrom,
      dateTo: yesterday < dateFrom ? dateFrom : yesterday,
    };
  }

  const previousMonth = moment.tz(BERLIN_TIMEZONE).subtract(1, "month");
  return {
    dateFrom: moment(previousMonth).startOf("month").format(SAVE_DATE_FORMAT),
    dateTo: moment(previousMonth).endOf("month").format(SAVE_DATE_FORMAT),
  };
};

/* True when [aFrom, aTo] and [bFrom, bTo] share at least one day. Plain
 * string compare is safe here: both sides are always `SAVE_DATE_FORMAT`
 * (YYYY-MM-DD), which sorts lexicographically the same as chronologically. */
const rangesOverlap = (
  aFrom: string,
  aTo: string,
  bFrom: string,
  bTo: string
): boolean => aFrom <= bTo && bFrom <= aTo;

/*
 * A hint, never validation — the export button stays enabled either way.
 *
 * The invoice number is YYMMDD of Von, so it only collides with a PAST run
 * whose own Von is the exact same date — overlapping the anchor's range is a
 * different, weaker fact and must not carry that claim. A warning that states
 * a shared invoice number when the numbers actually differ gets ignored
 * within two weeks, and two weeks later the same warning is ignored in the
 * case that really does produce a duplicate customer invoice. So the number
 * check runs first, against every past ranged run (not just the anchor): the
 * anchor is only the latest run, and the colliding Von could belong to an
 * earlier one.
 *
 * The overlap check that follows scans every ranged run too, comparing BOTH
 * ends of the selected range (SMT-1364 review): comparing only Von against
 * only the latest anchor could report a gap, or name the wrong export, once
 * an older period had been rerun and the selection actually fell inside or
 * across it rather than the latest run. The gap check stays anchored to the
 * latest run alone, because a gap is specifically about what has not been
 * covered SINCE that run — an older, non-adjacent selection that does not
 * overlap anything on record is neither a duplicate nor a gap worth naming.
 */
const computeWarning = (
  anchor: RunAnchor | null,
  rangedRuns: PastExportRun[],
  dateFrom: string | undefined,
  dateTo: string | undefined,
  t: TFunction
): string | null => {
  if (!anchor || !dateFrom) {
    return null;
  }

  const collidingRun = rangedRuns.find((run) => run.from === dateFrom);
  if (collidingRun) {
    return t("sameInvoiceNumberWarning", {
      range: formatRange(collidingRun.from, collidingRun.to),
      exportedAt: formatDateTime(collidingRun.changedAt),
    });
  }

  const effectiveDateTo = dateTo ?? dateFrom;
  const overlappingRun = rangedRuns.find((run) =>
    rangesOverlap(dateFrom, effectiveDateTo, run.from, run.to)
  );
  if (overlappingRun) {
    return t("overlapWarning", {
      range: formatRange(overlappingRun.from, overlappingRun.to),
      exportedAt: formatDateTime(overlappingRun.changedAt),
    });
  }

  const expectedFrom = moment(anchor.to).add(1, "day").format(SAVE_DATE_FORMAT);

  if (dateFrom <= expectedFrom) {
    return null;
  }

  return t("gapWarning", {
    range: formatRange(
      expectedFrom,
      moment(dateFrom).subtract(1, "day").format(SAVE_DATE_FORMAT)
    ),
  });
};

/*
 * Weekly EasyBill export (SMT-1364, superseding the monthly SMT-1277 version).
 * Cross-customer on purpose, which is why it lives here and not on the
 * Leistungstracker: that page is customer-scoped and its "Leistung erfassen"
 * button stays disabled until a customer is picked, so an always-enabled
 * export next to it would put two different data scopes side by side.
 *
 * Settings is the interim home. A dedicated billing area is the likely
 * destination; moving costs a nav entry and a route.
 */
export const ServiceTrackerExportView = () => {
  const { t, i18n } = useTranslation("ServiceTrackerExport");

  const [range, setRange] = useState<DateValues | null>(null);
  const [loading, setLoading] = useState<ServiceTrackerExportMode | null>(null);
  /*
   * The period is stored WITH the result, never read from the range picker
   * while the modal is open: the title must name the period the figures came
   * from. Reading live state let the modal relabel itself when the selection
   * changed, so it could claim one range over another's numbers.
   */
  const [preview, setPreview] = useState<{
    result: ServiceTrackerExportResult;
    dateFrom: string;
    dateTo: string;
  } | null>(null);

  /*
   * Set when the backend refuses an export as conflicting (kontax-backend#926,
   * HTTP 409). The period travels with it for the same reason it travels with
   * `preview` above: confirming must re-run exactly what was refused, not
   * whatever the date fields happen to hold by the time the operator decides.
   */
  const [conflict, setConflict] = useState<{
    dateFrom: string;
    dateTo: string;
    conflicts: ExportConflict[];
  } | null>(null);

  /*
   * Every export run is written to the audit trail by `logAdminCommand` under
   * recordId "ALL" (see the drawer below). This is the same query, fetched
   * eagerly instead of on drawer-open, and filtered client-side to this
   * command: it is both the source of the "Frühere Exporte" table and the
   * anchor the prefill and warnings compare against.
   */
  const [
    getExportHistory,
    {
      data: historyData,
      loading: historyLoading,
      called: historyCalled,
      error: historyError,
    },
  ] = useChangeLogsQuery({
    variables: {
      modelName: "AdminCommand",
      recordIds: [EXPORT_AUDIT_RECORD_ID],
    },
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    getExportHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * Re-run after every successful export (see handleExport) so a second
   * export in the same session — no reload — sees the run that was just
   * recorded: without this, only the query on mount ever populated the
   * history, and an operator exporting twice in a row never got the
   * shared-invoice-number warning for the second run.
   */
  const [historyRefreshing, setHistoryRefreshing] = useState(false);

  const { rangedRuns, lastRunAnchor } = useMemo(() => {
    const exportLogs = (historyData?.changeLogs ?? []).filter(
      (log) => log.changes?.__command?.name === EXPORT_COMMAND_NAME
    );

    const ranged = exportLogs
      .map(toPastExportRun)
      .filter((run): run is PastExportRun => run !== null);

    /*
     * The anchor is chosen across BOTH shapes by `changedAt`, not just
     * "the latest ranged run, or else the latest legacy run" — a newer
     * legacy run must not be shadowed by an older ranged one (SMT-1364
     * review). `rangedRuns` below stays ranged-only regardless: legacy runs
     * never appear in "Frühere Exporte", only ranged ones do.
     */
    const lastRunAnchor = exportLogs
      .map(toRunAnchor)
      .filter((anchor): anchor is RunAnchor => anchor !== null)
      .reduce<RunAnchor | null>((latest, anchor) => {
        if (!latest || anchor.changedAt > latest.changedAt) {
          return anchor;
        }
        return latest;
      }, null);

    return { rangedRuns: ranged, lastRunAnchor };
  }, [historyData]);

  // Compute the prefill once the history query has SUCCEEDED (found history,
  // or found none) — never on an error, which must leave `range` at null so
  // the date fields and the Preview/Export buttons stay disabled instead of
  // enabling with no collision or invoice-number warnings available at all.
  useEffect(() => {
    if (historyCalled && !historyLoading && !historyError && range === null) {
      setRange(computePrefill(lastRunAnchor));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyCalled, historyLoading, historyError]);

  const warning = useMemo(
    () =>
      computeWarning(
        lastRunAnchor,
        rangedRuns,
        range?.dateFrom,
        range?.dateTo,
        t
      ),
    [lastRunAnchor, rangedRuns, range?.dateFrom, range?.dateTo, t]
  );

  const busy = loading !== null || historyRefreshing || Boolean(historyError);

  const run = useCallback(
    async (
      mode: ServiceTrackerExportMode,
      forDateFrom: string,
      forDateTo: string,
      confirmConflicts?: boolean
    ) => {
      setLoading(mode);

      try {
        return confirmConflicts
          ? await serviceTrackerExportApi.getEasybillExport(
              forDateFrom,
              forDateTo,
              mode,
              true
            )
          : await serviceTrackerExportApi.getEasybillExport(
              forDateFrom,
              forDateTo,
              mode
            );
      } catch (error) {
        // A conflict is a decision for the operator, handled by the caller
        // via ConflictModal — not the generic error notification below.
        if (mode === "export" && error instanceof ExportConflictError) {
          throw error;
        }
        notification.error({
          message: t("error", { message: (error as Error).message }),
        });
        return null;
      } finally {
        setLoading(null);
      }
    },
    [t]
  );

  const handlePreview = useCallback(async () => {
    if (!range?.dateFrom || !range?.dateTo) {
      return;
    }

    const result = await run("preview", range.dateFrom, range.dateTo);
    if (result) {
      setPreview({ result, dateFrom: range.dateFrom, dateTo: range.dateTo });
    }
  }, [run, range]);

  /*
   * Always regenerates, including when triggered from inside the modal. Handing
   * back the previewed bytes would drop a correction made after previewing, and
   * that correction ("accidentally entered one euro") is the exact case the
   * preview exists to catch.
   *
   * The period is passed explicitly rather than read from state: exporting from
   * the modal must download what was reviewed, and the range picker was still
   * live while a preview request was in flight, which let someone review one
   * range and download another.
   */
  const handleExport = useCallback(
    async (
      forDateFrom: string,
      forDateTo: string,
      confirmConflicts = false
    ) => {
      let result: ServiceTrackerExportResult | null;
      try {
        result = await run("export", forDateFrom, forDateTo, confirmConflicts);
      } catch (error) {
        if (error instanceof ExportConflictError) {
          // Replace the preview with the conflict decision rather than
          // stacking modals — confirming re-runs the export and, on success,
          // closes this the same way a normal export closes the preview.
          setPreview(null);
          setConflict({
            dateFrom: forDateFrom,
            dateTo: forDateTo,
            conflicts: error.conflicts,
          });
          return;
        }
        // `run` already reports every other failure itself.
        return;
      }

      if (!result) {
        return;
      }

      setConflict(null);

      // Refetch the audit trail now that this run is (almost certainly)
      // written to it, and keep the controls busy until that settles — see
      // the comment on `historyRefreshing` above.
      setHistoryRefreshing(true);
      try {
        await getExportHistory();
      } finally {
        setHistoryRefreshing(false);
      }

      /*
       * The warnings from THIS run have to reach the operator, and this is the
       * only place they can: the audit-failure warning exists on the export
       * path alone, and data can have changed since the preview, so the file
       * being downloaded may carry problems the previewed figures never showed.
       * `duration: 0` because a billing warning should not time out.
       */
      if (result.warnings.length) {
        notification.warning({
          message: t("exportWarnings"),
          description: (
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {result.warnings.map((warningText) => (
                <li key={warningText}>{warningText}</li>
              ))}
            </ul>
          ),
          duration: 0,
        });
      }

      if (result.summary.positions === 0) {
        /*
         * An empty period and a period where every row was dropped look the
         * same from the position count, and telling an operator "nothing to
         * bill" when the truth is "everything was skipped" hides a data problem.
         */
        notification.info({
          message: result.warnings.length
            ? t("emptyPeriodAllSkipped")
            : t("emptyPeriod"),
        });
        return;
      }

      /*
       * Only an export carries the file. Reaching here without it means the
       * backend answered a preview to an export request, so say so rather than
       * downloading an empty file.
       */
      if (!result.csv) {
        notification.error({ message: t("error", { message: "missing CSV" }) });
        return;
      }

      downloadCSV(
        result.csv,
        `easybill-export-${forDateFrom}_${forDateTo}.csv`
      );
      setPreview(null);
    },
    [run, t, getExportHistory]
  );

  return (
    <ContentWrapper>
      <PageHeader>
        <Title>{t("title")}</Title>
        {/*
         * Every export run is written to the audit trail by `logAdminCommand`
         * under recordId "ALL", because the run is cross-customer and has no
         * single target. Same drawer the Command Hub uses.
         */}
        <ActionLogDrawerAsync
          title={String(t("logsTitle"))}
          modelName="AdminCommand"
          recordIds={[EXPORT_AUDIT_RECORD_ID]}
          selectedFields={["__command"]}
          renderButton={({ onClick }) => (
            <LogsButton type="link" onClick={onClick}>
              {t("logs")}
            </LogsButton>
          )}
        />
      </PageHeader>

      <Description>{t("description")}</Description>

      <Controls>
        <FilterBoxCard title={`${t("dateFrom")} / ${t("dateTo")}`}>
          <fieldset
            disabled={busy}
            style={{ border: 0, margin: 0, padding: 0 }}
          >
            <PeriodSelects>
              {/*
               * DateRangePicker (antd RangePicker) is UNCONTROLLED: it reads
               * `dateFrom`/`dateTo` into antd's `defaultValue` once, at mount,
               * and never again. Rendering it before the prefill is computed
               * (`range` still null while the export history is loading, or
               * has failed) would mount it with an empty default that a later
               * `range` update could never reach — the fields would stay
               * visibly empty forever while the state behind them already
               * held a real range, which is worse than no prefill (SMT-1364).
               * So it only mounts once there is a real value to seed it with.
               */}
              {range ? (
                <DateRangePicker
                  dateFrom={range.dateFrom}
                  dateTo={range.dateTo}
                  onChange={setRange}
                />
              ) : historyError ? null : (
                <Spin size="small" />
              )}
            </PeriodSelects>
          </fieldset>
        </FilterBoxCard>
        <Button
          onClick={handlePreview}
          loading={loading === "preview"}
          disabled={busy || !range?.dateFrom || !range?.dateTo}
        >
          {t("preview")}
        </Button>
        <Button
          type="primary"
          onClick={() =>
            range?.dateFrom &&
            range?.dateTo &&
            handleExport(range.dateFrom, range.dateTo)
          }
          loading={loading === "export"}
          disabled={busy || !range?.dateFrom || !range?.dateTo}
        >
          {t("export")}
        </Button>
      </Controls>

      {/*
       * Rendered here, OUTSIDE the `range ?` ternary above and outside the
       * `fieldset` it lives in, so it appears whether or not a range is
       * already set (e.g. the post-export history reload failing, SMT-1364
       * review) and its retry button stays clickable even though `busy` —
       * which now includes `historyError` — disables that fieldset.
       */}
      {historyError && (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 16, maxWidth: 720 }}
          message={t("historyError")}
          action={
            <Button size="small" onClick={() => getExportHistory()}>
              {t("retry")}
            </Button>
          }
        />
      )}

      {warning && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 16, maxWidth: 720 }}
          message={warning}
        />
      )}

      {preview && (
        <PreviewModal
          open
          exporting={loading === "export"}
          dateFrom={preview.dateFrom}
          dateTo={preview.dateTo}
          result={preview.result}
          onExport={() => handleExport(preview.dateFrom, preview.dateTo)}
          onClose={() => setPreview(null)}
        />
      )}

      {conflict && (
        <ConflictModal
          open
          confirming={loading === "export"}
          conflicts={conflict.conflicts}
          onConfirm={() =>
            handleExport(conflict.dateFrom, conflict.dateTo, true)
          }
          onCancel={() => setConflict(null)}
        />
      )}

      <HistorySection>
        <FilterBoxCard title={t("history.title")}>
          <Table<PastExportRun>
            size="small"
            pagination={false}
            loading={historyLoading}
            rowKey={(row) => `${row.from}-${row.to}-${row.changedAt}`}
            dataSource={rangedRuns}
            columns={[
              {
                title: t("history.colRange"),
                key: "range",
                render: (_value, row) => formatRange(row.from, row.to),
              },
              {
                title: t("history.colExportedAt"),
                key: "exportedAt",
                render: (_value, row) => formatDateTime(row.changedAt),
              },
              {
                title: t("history.colExportedBy"),
                dataIndex: "changedBy",
                key: "exportedBy",
              },
              {
                title: t("history.colPositions"),
                key: "positions",
                align: "right",
                render: (_value, row) => row.summary.positions,
              },
              {
                title: t("history.colGrossTotal"),
                key: "grossTotal",
                align: "right",
                render: (_value, row) =>
                  formatEuro(row.summary.grossTotalInEuro, i18n.language),
              },
            ]}
          />
        </FilterBoxCard>
      </HistorySection>
    </ContentWrapper>
  );
};
